import { Circle, Divider, HStack, SquareProps, StackProps } from '@chakra-ui/react';
import { Icon } from '@common/components/Icon';
import * as React from 'react';
import { Dispatch, SetStateAction, useCallback, useMemo, useState } from 'react';

interface Helpers {
  goToNextStep: () => void;
  goToPrevStep: () => void;
  reset: () => void;
  canGoToNextStep: boolean;
  canGoToPrevStep: boolean;
  setStep: Dispatch<SetStateAction<number>>;
}

interface UseStepProps {
  maxStep: number;
  initialStep?: number;
}

export const useStep = (props: UseStepProps): [number, Helpers] => {
  const { maxStep, initialStep = 0 } = props;
  const [currentStep, setCurrentStep] = useState(initialStep);
  const canGoToNextStep = useMemo(() => currentStep + 1 <= maxStep, [currentStep, maxStep]);
  const canGoToPrevStep = useMemo(() => currentStep - 1 >= 0, [currentStep]);

  const setStep = useCallback(
    (step: unknown) => {
      const newStep = step instanceof Function ? step(currentStep) : step;
      if (newStep >= 0 && newStep <= maxStep) {
        setCurrentStep(newStep);
        return;
      }
      throw new Error('Step not valid');
    },
    [maxStep, currentStep],
  );

  const goToNextStep = useCallback(() => {
    if (canGoToNextStep) {
      setCurrentStep((step) => step + 1);
    }
  }, [canGoToNextStep]);

  const goToPrevStep = useCallback(() => {
    if (canGoToPrevStep) {
      setCurrentStep((step) => step - 1);
    }
  }, [canGoToPrevStep]);

  const reset = useCallback(() => {
    setCurrentStep(0);
  }, []);

  return [
    currentStep,
    {
      canGoToNextStep,
      canGoToPrevStep,
      goToNextStep,
      goToPrevStep,
      reset,
      setStep,
    },
  ];
};

interface RadioCircleProps extends SquareProps {
  isCompleted: boolean;
  isActive: boolean;
}

export const StepCircle: React.FC<RadioCircleProps> = ({ isCompleted, isActive }) => {
  return isActive ? (
    <Circle padding="3px" bg={isCompleted ? 'blue.500' : 'white'} borderWidth={'4px'} borderColor={'blue.500'}>
      <Circle bg="blue.500" size="7px" />
    </Circle>
  ) : (
    <Circle
      padding={'3px'}
      bg={isCompleted ? 'blue.500' : 'white'}
      borderWidth={'2px'}
      borderColor={isCompleted ? 'blue.500' : 'gray.100'}
      borderTopColor={isActive || isCompleted ? 'blue.500' : 'gray.100'}>
      {isCompleted ? (
        <Icon icon="check" color="white" boxSize="10px" />
      ) : (
        <Icon icon="check" color="white" boxSize="10px" />
      )}
    </Circle>
  );
};

interface StepProps extends StackProps {
  isCompleted: boolean;
  isActive: boolean;
  isLastStep: boolean;
}

export const Step: React.FC<StepProps> = ({ isCompleted, isLastStep, isActive }) => {
  return (
    <HStack flex={isLastStep ? '0' : '1'} spacing="0">
      <StepCircle isActive={isActive} isCompleted={isCompleted} />
      {!isLastStep && (
        <Divider
          orientation="horizontal"
          opacity={1}
          borderWidth="2px"
          borderColor={isCompleted ? 'blue.500' : 'gray.100'}
        />
      )}
    </HStack>
  );
};

type ProgressBarProps = {
  currentStep: number;
  totalSteps: number;
};

export const ProgressBar: React.FC<ProgressBarProps> = ({ currentStep, totalSteps }) => {
  return (
    <HStack spacing="0" justify="space-evenly" flex="1" mt={'5px'}>
      {[...Array(4)].map((_, id) => (
        <Step
          key={id}
          isActive={currentStep === id}
          isCompleted={currentStep > id}
          isLastStep={id + 1 === totalSteps}
        />
      ))}
    </HStack>
  );
};
